/* src/pages/ManageDonation/index.jsx */

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./index.scss";
import { useSelector } from "react-redux";
import { getSchedule } from "../../api/payments";
import CustomButton from "../../components/CustomButton";
import moment from "moment-timezone";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useTranslation } from "react-i18next";
import {
  getTemplateDisplayName,
  isFridayTemplateKey
} from "../../utils/templateUtils";

const ManageDonation = () => {
  const { t } = useTranslation();
  const Funds = useSelector((state) => state.donation.funds);
  const { scheduleId } = useParams();

  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track copy confirmation in state to show a short success message
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const data = await getSchedule(scheduleId);
        setSchedule(data);
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [scheduleId]);

  // Copy URL handler
  const handleCopyUrl = async () => {
    try {
      // Copy the current page's URL to clipboard
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);

      // Hide the "Copied!" message after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy the URL:", error);
    }
  };

  if (loading) {
    return (
      <div className="manage-donation-container">
        <p>{t("manageDonationLoading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-donation-container">
        <h2>{t("manageDonationTitle")}</h2>
        <p className="error">
          {t("manageDonationError")}: {error}
        </p>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="manage-donation-container">
        <h2>{t("manageDonationTitle")}</h2>
        <p className="error">
          {t("manageDonationError")}: Missing schedule data
        </p>
      </div>
    );
  }

  const {
    donorDetails,
    template,
    donationScheduleBreakdown = [],
    status,
    timezone: scheduleTimezone
  } = schedule;
  const tz = scheduleTimezone || "America/Toronto";
  const isFridayTemplate = isFridayTemplateKey(template);

  const firstBucketTotal = isFridayTemplate
    ? donationScheduleBreakdown?.[0]?.allocations?.reduce(
        (sum, alloc) => sum + (alloc.amount || 0),
        0
      )
    : 0;

  // Calculate total / fulfilled amounts
  const totalAmount = donationScheduleBreakdown.reduce((sum, donation) => {
    const donationSum = donation.allocations.reduce(
      (aSum, alloc) => aSum + Number(alloc.amount),
      0
    );
    return sum + donationSum;
  }, 0);

  const fulfilledAmount = donationScheduleBreakdown.reduce((sum, donation) => {
    const completedSum = donation.allocations.reduce(
      (acc, alloc) =>
        alloc.status === "completed" ? acc + Number(alloc.amount) : acc,
      0
    );
    return sum + completedSum;
  }, 0);

  // Next donation date (ignore dates with $0 allocation)
  // Use schedule timezone so "next" and display match the donor's charge time (e.g. 10 PM local)
  const nowInTz = moment().tz(tz).startOf("day");
  const futureDonations = donationScheduleBreakdown.filter((donation) => {
    const donationDayInTz = moment(donation.date).tz(tz).startOf("day");
    const isFuture = !donationDayInTz.isBefore(nowInTz);
    const dayTotal = donation.allocations.reduce(
      (sum, alloc) => sum + (alloc.amount || 0),
      0
    );
    return isFuture && dayTotal > 0;
  });

  const nextDonationDate =
    futureDonations.length > 0
      ? moment(futureDonations[0].date).tz(tz).format("MMM DD, YYYY")
      : t("manageDonationNoFutureDonations");

  // Collect cause names
  let causeNamesSet = new Set();
  if (donationScheduleBreakdown.length > 0) {
    const firstDonation = donationScheduleBreakdown[0];
    causeNamesSet = new Set(
      firstDonation.allocations.map((alloc) => {
        const foundFund = Funds
          ? Funds.find((f) => f.fundId === alloc.fundId)
          : null;
        const causeName = foundFund
          ? foundFund.name
          : t("manageDonationUnknownCause");
        return causeName;
      })
    );
  }

  // Prepare data for the table (only include days with amounts > 0)
  // Format dates in schedule timezone for consistency with "Next Donation Date"
  const aggregatedDonations = donationScheduleBreakdown
    .map((donation) => {
      const dateFormatted = moment(donation.date).tz(tz).format("MMM DD, YYYY");
      const dayTotal = donation.allocations.reduce(
        (sum, alloc) => sum + alloc.amount,
        0
      );
      const allCompleted = donation.allocations.every(
        (alloc) => alloc.status === "completed"
      );
      const dayStatus = allCompleted ? "completed" : "pending";
      return {
        date: dateFormatted,
        amount: dayTotal,
        status: dayStatus
      };
    })
    .filter((entry) => entry.amount > 0);

  const percentage =
    totalAmount === 0 ? 0 : (fulfilledAmount / totalAmount) * 100;

  return (
    <div className="manage-donation-container">
      <h2>
        {isFridayTemplate
          ? t("manageDonationFridayTitle")
          : t("manageDonationTitle")}
      </h2>

      <div className="top-row">
        <div className="schedule-info-card">
          <div className="info-row">
            <div className="info-label">{t("manageDonationDonorLabel")}:</div>
            <div className="info-value">
              {donorDetails?.firstName} {donorDetails?.lastName}
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">{t("manageDonationEmailLabel")}:</div>
            <div className="info-value">{donorDetails?.email}</div>
          </div>
          <div className="info-row">
            <div className="info-label">{t("manageDonationStatusLabel")}:</div>
            <div className="info-value">
              <span className={`status-pill ${status}`}>{status}</span>
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">
              {t("manageDonationTemplateLabel")}:
            </div>
            <div className="info-value">
              {isFridayTemplate
                ? "Fridays"
                : getTemplateDisplayName(template)}
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">{t("manageDonationCausesLabel")}:</div>
            <div className="info-value">
              {causeNamesSet.size === 0
                ? "N/A"
                : Array.from(causeNamesSet).map((causeName) => {
                    const foundFund = Funds
                      ? Funds.find((f) => f.name === causeName)
                      : null;
                    const icon = foundFund && foundFund.iconSVGCode && (
                      <span
                        className="cause-icon"
                        dangerouslySetInnerHTML={{
                          __html: foundFund.iconSVGCode
                        }}
                      />
                    );

                    return (
                      <div className="cause-item" key={causeName}>
                        {icon && (
                          <span className="cause-icon-wrapper">{icon}</span>
                        )}
                        <span className="cause-name">{causeName}</span>
                      </div>
                    );
                  })}
            </div>
          </div>
          {!isFridayTemplate && (
            <div className="info-row">
              <div className="info-label">
                {t("manageDonationTotalAmountLabel")}:
              </div>
              <div className="info-value">${totalAmount.toFixed(2)}</div>
            </div>
          )}

          <div className="info-row">
            <div className="info-label">
              {isFridayTemplate
                ? t("manageDonationNextFridayDateLabel")
                : t("manageDonationNextDateLabel")}
              :
            </div>
            <div className="info-value">
              {nextDonationDate} -{" "}
              {isFridayTemplate ? "$" + firstBucketTotal : ""}
            </div>
          </div>
        </div>

        <div className="progress-card">
          <div className="progress-container">
            <CircularProgressbar
              value={percentage}
              text={`${percentage.toFixed(0)}%`}
              styles={buildStyles({
                pathColor: "#38D572",
                textColor: "#38D572",
                trailColor: "#ddd",
                backgroundColor: "#f5f5f5"
              })}
            />
          </div>
          <p className="progress-text">
            {t("manageDonationFulfillmentProgress")}
          </p>
        </div>
      </div>

      <h3>
        {isFridayTemplate
          ? t("manageDonationDonationBreakdownHeaderFriday")
          : t("manageDonationDonationBreakdownHeader")}
      </h3>
      {isFridayTemplate ? (
        <div className="donation-table-container">
          {(() => {
            // For Fridays, find the next upcoming payment from filtered donations
            const todayInTz = moment().tz(tz).startOf("day");
            const upcomingFriday = aggregatedDonations
              .filter((entry) => {
                const entryDate = moment.tz(entry.date, "MMM DD, YYYY", tz);
                return entryDate.isAfter(todayInTz) || entryDate.isSame(todayInTz);
              })
              .sort((a, b) =>
                moment.tz(a.date, "MMM DD, YYYY", tz).diff(
                  moment.tz(b.date, "MMM DD, YYYY", tz)
                )
              )
              [0];

            if (!upcomingFriday) {
              return (
                <p style={{ padding: "1rem" }}>
                  {t("manageDonationNoDonationsScheduledFriday")}
                </p>
              );
            }

            return (
              <table className="donation-table">
                <thead>
                  <tr>
                    <th>{t("manageDonationDateColumn")}</th>
                    <th>{t("manageDonationAmountColumn")}</th>
                    <th>{t("manageDonationStatusColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{upcomingFriday.date}</td>
                    <td>${upcomingFriday.amount.toFixed(2)}</td>
                    <td>
                      <span className={`status-pill ${upcomingFriday.status}`}>
                        {upcomingFriday.status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div>
      ) : (
        <div className="donation-table-container">
          {aggregatedDonations.length === 0 ? (
            <p style={{ padding: "1rem" }}>
              {t("manageDonationNoDonationsScheduled")}
            </p>
          ) : (
            <table className="donation-table">
              <thead>
                <tr>
                  <th>{t("manageDonationDateColumn")}</th>
                  <th>{t("manageDonationAmountColumn")}</th>
                  <th>{t("manageDonationStatusColumn")}</th>
                </tr>
              </thead>
              <tbody>
                {aggregatedDonations.map((entry, i) => (
                  <tr key={i}>
                    <td>{entry.date}</td>
                    <td>${entry.amount.toFixed(2)}</td>
                    <td>
                      <span className={`status-pill ${entry.status}`}>
                        {entry.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Example: Bookmark + Copy URL actions */}
      <div className="bookmark-row">
        <CustomButton
          text="Copy Page URL"
          onClick={handleCopyUrl}
          buttonStyle={{
            padding: "1rem"
          }}
        />
      </div>
      {copied && <p style={{ color: "green" }}>Copied URL to clipboard!</p>}

      {/* For 'pending' status, if you want a Cancel button */}
      {status === "cancelled" && (
        <p className="info-message">{t("manageDonationDonationCancelled")}</p>
      )}
      {status === "completed" && (
        <p className="info-message">{t("manageDonationDonationCompleted")}</p>
      )}
    </div>
  );
};

export default ManageDonation;
